import {type ReactElement, useEffect, useState} from 'react';

import {TimeLogger} from 'utils/logger';

import {ErrorFallback} from './error_fallback';

type Props = {
    children: ReactElement;
}

const IGNORED_ERROR_MESSAGES = new Set([
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
]);

export const GlobalErrorCatcher = ({children}: Props) => {
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            if (IGNORED_ERROR_MESSAGES.has(event.message)) {
                return;
            }

            setError(true);
            TimeLogger.error(event.message, {
                extra: {
                    message: event.message,
                    name: event.error?.name ?? event.message,
                    stack: event.error?.stack,
                },
            });
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            let name = 'unhandledrejection';
            let message;
            let stack;

            if (event.reason instanceof Error) {
                message = event.reason.message;
                name = event.reason.name;
                stack = event.reason.stack || '';
            } else {
                message = String(event.reason);
                stack = '';
            }

            setError(true);
            TimeLogger.error(message, {extra: {name, stack}});
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    return error ? <ErrorFallback /> : children;
};
